import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { BmMgmFeeGroupForm, BmItemForm, BmMgmFeeValuesForm } from "../../../pojo/bm-form";
import { BmItem } from "../../../pojo/bm-classes";
import { BmCalcHelperService } from "../../general/bm-calc-helper.service";
import { BmDefaultFormCreatorService } from "../../general/bm-default-form-creator.service";

/**
 * SERVICE to manage row MGM FEE VALUES logic in Management Fees Group
 *
 * This service updates the "mgmFeeValues" subGroup data based on
 *  - commitment
 *  - mgmFeesPcnt
 *  - pcntOfMgmFee (if not selected 100% will be used)
 *
 * as:  mgmFeeValue = commitment * mgmFeesPcnt * (pcntOfMgmFee / 100).
 */
export class MgmFeeValuesFormService {

  public static createMgmFeeValuesForm(yearsNumber: number): FormGroup<BmMgmFeeValuesForm> {
    const items: BmItem[] = BmDefaultFormCreatorService.createItemsArray(yearsNumber);
    const itemsFa = new FormArray<FormGroup<BmItemForm>>(items.map((item) => BmCalcHelperService.createBmItemForm(item)));
    return new FormGroup<BmMgmFeeValuesForm>({
      items: itemsFa,
      totalRow: new FormControl<number>(0),
      totalRowPcnt: new FormControl<number>(0),
    });
  }

  /**
   * For each item:
   *  - if it's QUARTER => mgmFee = commitmentVal*(mgmFeesPcntVal/100)*(pcntVal/100)
   *  - if it's YEAR => sum of that year's QUARTER mgmFees
   *
   *  If pcnt of mgm Fee is not selected => use 100% in formula
   */
  public static mgmFeeValuesRecalculate(mgmFeeGroupFG: FormGroup<BmMgmFeeGroupForm>): void {
    const mgmFeeValuesForm = mgmFeeGroupFG.controls.subGroups.controls.mgmFeeValues;
    const items = mgmFeeValuesForm.controls.items;
    const totalRow = mgmFeeValuesForm.controls.totalRow;

    // References to the other subGroups
    const commitmentFA = mgmFeeGroupFG.controls.subGroups.controls.commitment.controls.items;
    const mgmFeesPcntFA = mgmFeeGroupFG.controls.subGroups.controls.mgmFeesPcnt.controls.items;
    const pcntOfMgmFeeFA = mgmFeeGroupFG.controls.subGroups.controls.pcntOfMgmFees.controls.items;
    const isPcntOfMgmFeeSelected = mgmFeeGroupFG.controls.subGroups.controls.pcntOfMgmFees.controls.isSelected.getRawValue();
    const itemsByYear = BmCalcHelperService.groupItemsByYear(items);

    itemsByYear.forEach(({ yearFG, quartersFG }) => {
      quartersFG.forEach((quarter) => {
        const qNum = quarter.getRawValue().period.quarterNumber;
        const yNum = yearFG.getRawValue().period.yearNumber;

        // 1) find corresponding commitment item
        const commitItem = BmCalcHelperService.findItem(commitmentFA, qNum, yNum);
        const commitVal = commitItem ? commitItem.getRawValue().itemValue : 0;

        // 2) find corresponding mgm fee pcnt item
        const mgmFeesPcntItem = BmCalcHelperService.findItem(mgmFeesPcntFA, qNum, yNum);
        const mgmFeesPcnt = mgmFeesPcntItem ? mgmFeesPcntItem.getRawValue().itemValue : 0;

        // 3) find corresponding pcnt of mgm fee item IF SELECTED, otherwise SET 100%
        let pcntOfMgmFee: number;
        if (isPcntOfMgmFeeSelected) {
          const pcntItem = BmCalcHelperService.findItem(pcntOfMgmFeeFA, qNum, yNum);
          pcntOfMgmFee = pcntItem ? pcntItem.getRawValue().itemValue : 0;
        } else {
          pcntOfMgmFee = 100;
        }

        // 4) mgm fee value formula for quarter
        const newVal = commitVal * (mgmFeesPcnt / 100) * (pcntOfMgmFee / 100);
        quarter.controls.itemValue.patchValue(newVal, { emitEvent: false });
      });
      const sumQuarters = BmCalcHelperService.calculateSumOfQuarterValues(quartersFG);
      yearFG.controls.itemValue.patchValue(sumQuarters, { emitEvent: false });
    });

    totalRow.patchValue(BmCalcHelperService.recalculateRowTotal(items), { emitEvent: false });
  }
}
